/**
 * 语文听写模块的输入校验（zod，前后端共用）。
 *
 * 与根目录 `schemas.ts` 的分工：本文件只装听写模块的东西，避免那个已经很长的
 * 文件继续膨胀。两者导出方式一致（`@fht/shared` 的 index 一起 `export *`）。
 * 刻意**不**从 `../schemas.ts` 反向 import：那里已经有几百行，本模块只需要
 * 两三个最基础的 helper，复制定义比制造模块间耦合更省事（且不会有循环依赖）。
 *
 * ## 与导入解析器的分工
 *
 * 导入的**逐行校验**在 `./import-parser.ts`（纯函数，能给出"第几行、什么问题"），
 * 这里只校验**请求体本身**：字段名字对不对、字符串长不长、枚举值合不合法。
 * 两者刻意不合并：把 200 行的解析错误塞进 zod 的 issues 里，前端只能拿到
 * 一串 path 数组，做不出"逐行报告"那张表。
 */
import { z } from 'zod';
import { GRADE_CODES } from '../constants.js';
import { WORD_CATEGORIES } from './import-parser.js';
import { HANZI_LIST_TYPES, LIST_TYPES, MAX_HIDDEN_READINGS } from './listing.js';
import { TEXTBOOK_REF_MAX } from './textbook-identity.js';
import {
  PUBLISHER_NAME_MAX,
  isGeneratedPublisherCode,
} from './publisher.js';
import { dictationTempoSchema } from './tempo.js';
import { DICTATION_MARKS } from './schedule.js';
import {
  DICTATION_AUDIO_MAX_BASE64,
  base64DecodedBytes,
} from './recording.js';

/** 学期枚举（与数据库 `semester` 一致）。 */
export const SEMESTERS = ['上', '下', '不分'] as const;
export type Semester = (typeof SEMESTERS)[number];

/** 学科（目前只有语文；英语听写是未来的事，先留出口子）。 */
export const DICTATION_SUBJECTS = ['chinese'] as const;

const idSchema = z.string().uuid('必须是合法的 id');
const idListSchema = z.array(idSchema).max(500, '一次最多筛 500 个 id');

/**
 * 年级枚举。`GRADE_CODES` 是 `GradeCode[]`（不是元组），zod 的 `z.enum` 要元组，
 * 所以这里 spread 后断言 —— 它是**运行期数组**，语法上不可能为空。
 */
const gradeCodeSchema = z.enum([...GRADE_CODES] as [string, ...string[]], {
  /** 中文提示：漏传 / 传了未知 code 时都给人话（界面上是下拉，正常到不了这里）。 */
  errorMap: () => ({ message: '请选择年级（课本按「册」建，一年级上册和一年级下册是两本书）' }),
});

/**
 * 单字拼音的形状（与数据库 CHECK 同口径）：不含大写、数字、空白。
 * 放在 zod 里是为了让错误在**请求校验阶段**就带中文提示返回，
 * 而不是让数据库的 23514 变成一句笼统的"提交的内容有误"。
 */
const singlePinyinSchema = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .regex(/^[^A-Z0-9\s]*$/, '拼音只能用小写字母加声调符号，不能有大写、数字或空格');

/**
 * 整词拼音：允许用空格分隔音节，但**每个音节**都必须是"小写字母 + 声调符号"。
 *
 * 音节数是否与字数一致由 `validateWordPinyin()` 判定（它会给出"少了一个音节"
 * 这类具体原因）；zod 这一层只挡住"数字声调（hu2）、大写、奇怪的符号"。
 */
const wordPinyinSchema = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .refine(
      (v) => v === '' || v.split(/\s+/).every((s) => /^[^A-Z0-9\s]+$/.test(s)),
      '每个音节只能用小写字母加声调符号（不要用数字标声调，例如应写 hú 而不是 hu2）',
    );

/** 查询串里的布尔值（与根 schemas 的 `queryBooleanSchema` 同口径）。 */
const queryBooleanSchema = z
  .enum(['true', 'false', '1', '0'])
  .transform((v) => v === 'true' || v === '1');

// ---------------------------------------------------------------- 课本 / 课文

/** 课本：出版社 + 年级 + 学期（**一册 = 一本书**）。版次已彻底不用。 */
export const createTextbookSchema = z.object({
  /**
   * **出版社名**（人话：`人民教育出版社`）—— 2026-10-18 起库里的标准值就是它。
   *
   * 改造前这一格要"代号"（`^[a-z][a-z0-9_]*$`），于是弹窗那条路把
   * `人民教育出版社` 换算成 `pub_be07fb11` 写库，而导入自动建书写的是人话
   * ⇒ 同一家出版社两个值、下拉两项、各查到一半的字（生产实测）。
   *
   * ⚠️ 仍然接受历史代号形状（`renjiao` / `RenJiao`）—— 归一后小写去空白，
   *    与 `textbookRefKey()` 同口径（服务层 `canonicalPublisherName()` 做这件事）。
   * ⛔ 但 `pub_xxxxxxxx`（由人话哈希出来的历史代号）**直接拒**：它不可逆，
   *    再写一次就是把 bug 又种一遍。那句话告诉用户该写什么。
   */
  publisher: z
    .string()
    .trim()
    .min(1, '请填写出版社（书封面 / 版权页上的那家，例如 人民教育出版社）')
    .max(PUBLISHER_NAME_MAX, `出版社名太长了（最多 ${PUBLISHER_NAME_MAX} 个字）`)
    .refine(
      (value) => !isGeneratedPublisherCode(value),
      '出版社请写名称（例如 人民教育出版社）：pub_ 开头的那串是历史代号，不要再填',
    ),
  /**
   * **年级**（`primary_1` …）—— **必填**，课本身份的一列（2026-10-18 起）。
   *
   * 一本课本 = 一个具体的册：一年级上册、一年级下册是**两本书**，分别建。
   */
  gradeCode: gradeCodeSchema,
  /** **学期**（`上` / `下` / `不分`）—— **必填**，课本身份的另一列。 */
  semester: z.enum(SEMESTERS, {
    errorMap: () => ({ message: '请选择学期（上册 / 下册）' }),
  }),
  /** 展示名（"人教版（部编）"）。 */
  name: z.string().trim().min(1, '请填写课本名称').max(64),
  subject: z.enum(DICTATION_SUBJECTS).default('chinese'),
  isDefault: z.boolean().default(false),
  sortOrder: z.number().int().min(0).max(9999).default(0),
});

export const updateTextbookSchema = createTextbookSchema
  .partial()
  .refine((v) => Object.keys(v).length > 0, '没有需要更新的内容');

/** 课文（`lesson`）。 */
export const createLessonSchema = z.object({
  textbookId: idSchema,
  gradeCode: gradeCodeSchema,
  semester: z.enum(SEMESTERS),
  /** 课序原始写法（"5" / "识字1" / "语文园地三"）。 */
  lessonLabel: z.string().trim().max(32).default(''),
  /** 课序数字（排序用），不填则从 `lessonLabel` 里取第一个数字。 */
  lessonNo: z.number().int().min(0).max(999).optional(),
  title: z.string().trim().min(1, '请填写课题').max(128),
  unitNo: z.number().int().min(1).max(99).nullable().default(null),
  sortOrder: z.number().int().min(0).max(9999).default(0),
});

export const updateLessonSchema = createLessonSchema
  .omit({ textbookId: true })
  .partial()
  .refine((v) => Object.keys(v).length > 0, '没有需要更新的内容');

// ---------------------------------------------------------------- 字 / 词

/**
 * 汉字（`hanzi`）。
 *
 * `pinyin` 是**人工覆盖值**，不是最终值：传空串等于"清除覆盖、回到自动值"
 * （见 shared/dictation/pinyin.ts 的 resolvePinyin）。服务层据此写
 * `pinyin` / `pinyin_overridden` 两列。
 *
 * ⚠️ **没有 `category` 了**（迁移 0009 删列）。"这个字在哪张表上"改成
 * `listType` + `hanzi_listing`：**一个字可以同时有两行收录**（识字表 + 写字表）。
 */
export const createHanziSchema = z.object({
  textbookId: idSchema,
  charText: z
    .string()
    .trim()
    .refine((v) => [...v].length === 1, '必须正好一个汉字'),
  /** 覆盖拼音；省略或空串 = 用内置字表的自动读音。 */
  pinyin: singlePinyinSchema(32).optional(),
  gradeCode: gradeCodeSchema.nullable().default(null),
  semester: z.enum(SEMESTERS).nullable().default(null),
  /**
   * 手工加这个字时，**顺手收录到哪张表**（默认写字表 —— 家长手工加的通常是
   * "今天要听写的那个字"，而听写的默认预期是"要会写"）。
   *
   * 传 `null` = **只建字、不收录**（这个字在这一册上不属于任何一张表）。
   * 想同时收录到两张表时，先建字，再各调一次 `POST .../hanzi/:id/listings`。
   */
  listType: z.enum(HANZI_LIST_TYPES).nullable().default('xiezi'),
  /** 收录的年级/学期；省略时用 `gradeCode` / `semester`（同一册）。 */
  listGradeCode: gradeCodeSchema.optional(),
  listSemester: z.enum(SEMESTERS).optional(),
  note: z.string().trim().max(200).default(''),
  /** 关联课文（可多课）。 */
  lessonIds: idListSchema.optional(),
});

export const updateHanziSchema = z
  .object({
    pinyin: singlePinyinSchema(32).nullable().optional(),
    gradeCode: gradeCodeSchema.nullable().optional(),
    semester: z.enum(SEMESTERS).nullable().optional(),
    note: z.string().trim().max(200).optional(),
    /** 给了就**整组替换**关联课文（不是追加）—— 与作业整单保存同一思路。 */
    lessonIds: idListSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, '没有需要更新的内容');

// ---------------------------------------------------------------- 收录（识字表 / 写字表）

/**
 * 新建一条收录：**这个字在这本书这一册的这张表上**。
 *
 * ⚠️ 同一个字在两张表上要建**两条**；重复建同一条会被唯一键挡下（幂等）。
 */
export const createHanziListingSchema = z.object({
  hanziId: idSchema,
  textbookId: idSchema,
  gradeCode: gradeCodeSchema,
  semester: z.enum(SEMESTERS),
  listType: z.enum(HANZI_LIST_TYPES),
  /** 在这张表里的顺序（照课本附录原样保留）。 */
  sortOrder: z.number().int().min(0).max(9999).default(0),
});

/** 改一条收录（"这个字到底在哪张表上"录错了）。`hanziId` / `textbookId` 不可改。 */
export const updateHanziListingSchema = z
  .object({
    gradeCode: gradeCodeSchema.optional(),
    semester: z.enum(SEMESTERS).optional(),
    listType: z.enum(HANZI_LIST_TYPES).optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, '没有需要更新的内容');

/**
 * **整组替换一个字的收录**（`PUT .../hanzi/:hanziId/listings`）。
 *
 * 语义与"整组替换关联课文"（`lessonIds`）一致：**给了什么就是什么**，
 * 空数组 = 这个字不属于任何一张表。
 *
 * ⚠️ `textbookId` **必须在每一项里**（而不是外层的默认值）：收录记录挂在哪本书上
 *    是这一行的身份之一，服务层会校验它必须与这个字所属的课本一致。
 */
export const replaceHanziListingsSchema = z.object({
  listings: z
    .array(
      z.object({
        textbookId: idSchema,
        gradeCode: gradeCodeSchema,
        semester: z.enum(SEMESTERS),
        listType: z.enum(HANZI_LIST_TYPES),
        sortOrder: z.number().int().min(0).max(9999).optional(),
      }),
    )
    .max(24, '一个字最多 24 条收录（12 个年级 × 2 张表）'),
});

/**
 * **按书 + 按表整块粘贴**（用户的实际录入姿势：照课本后面的附录表粘）。
 *
 * 与 `/import/preview` 的关系：那条路是"一份文本里什么都能有"的通用入口，
 * 这条路是**一本书的一张表**的专用入口 —— 家长不需要在每一行重复写
 * 课本/年级/学期/表名，也不会因为少写一列就落错地方。
 *
 * `text` 里可以：
 *   · 只有字（`天地人你我他` / `天 地 人` / `天\t地\t人`）；
 *   · 每行 `字 拼音` 或 `字:拼音`（给这个字一个读音覆盖）；
 *   · 每行 `字 词` 或 `字\t词`（**顺手给这个字造词**）。
 * 解析规则与通用导入**共用同一个解析器**（`parseDictationImport`），
 * 所以"逐行报错 + 有效行照常导入"的语义完全一致。
 */
export const listImportSchema = z
  .object({
    /**
     * 要录进哪一册：**两种给法，二选一**。
     *
     *   1. `textbookId` —— 书已经在列表里（界面上三格定位到了它）；
     *   2. `publisher`  —— 只给了「出版社 + 年级 + 学期」，**书还不存在也认**：
     *      服务层按 `(publisher, gradeCode, semester)` 解析，提交时不存在就新建
     *      （唯一键 `(publisher, subject, grade_code, semester)` + `onConflictDoNothing`
     *      + 再查一次 ⇒ 并发下也只有一本，重复录入不会建第二本）。
     *
     * ⚠️ `publisher` 收的是**界面上下拉里那串人话**（`人民教育出版社`），不是代号 ——
     *    2026-10-18 起它也是**写库的标准值**（`canonicalPublisherName()`）。
     *    历史行里 `publisher` 是 `pub_xxxxxxxx`、人话在 `name` 那一列，服务层按
     *    `publisher` **或** `name` 匹配，所以两种形状都认得出来。
     * ⛔ `pub_xxxxxxxx` 这种代号**直接拒**：它不可逆，再写一次就是又种一遍 bug。
     */
    textbookId: idSchema.optional(),
    publisher: z
      .string()
      .trim()
      .min(1, '请选出版社')
      .max(TEXTBOOK_REF_MAX)
      .refine(
        (value) => !isGeneratedPublisherCode(value),
        '出版社请写名称（例如 人民教育出版社）：pub_ 开头的那串是历史代号，不要再填',
      )
      .optional(),
    /**
     * 年级 / 学期（**必填**）。
     *
     * ⚠️ 2026-10-18 起课本本身就是**一册**（`textbook.grade_code` / `semester`）。
     *    给 `textbookId` 时这两个值必须与那本书一致（路由核对，不一致报 400）；
     *    给 `publisher` 时它们**就是**这本书的年级 / 学期（不存在时按这两个值建）。
     */
    gradeCode: gradeCodeSchema,
    semester: z.enum(SEMESTERS),
    listType: z.enum(LIST_TYPES),
    /** 整块粘贴的正文。 */
    text: z.string().min(1, '请粘贴要录入的内容').max(200_000, '一次最多 20 万字符'),
    /** 词条的默认类别（录词语表时用；留空按"词语"）。 */
    wordCategory: z.enum(WORD_CATEGORIES).optional(),
    /**
     * **预览里给重复出现的字选的读音**（迁移 0018，2026-10-19）。
     *
     * 用户点字选音之后，界面把这些 `{line,index,reading}` 连同**原来那份 text**
     * 一起发回来 —— 所以预览与提交仍然是同一份文本，读音只是附加标注。
     *
     * `line` = 原文行号（1 起）；`index` = **这一行的第几个字**（1 起，只数汉字）。
     */
    readings: z
      .array(
        z.object({
          line: z.number().int().min(1),
          index: z.number().int().min(1),
          reading: singlePinyinSchema(32).refine((value) => value !== '', '读音不能是空的'),
        }),
      )
      .max(5000, '一次最多给 5000 个位置标音')
      .optional(),
    /** 是否先预览（不写库）。`true` = 只解析 + 查库比对。 */
    dryRun: z.boolean().default(false),
    /** 一次最多写多少行（`dryRun=false` 时生效）。 */
    maxRows: z.number().int().min(1).max(5000).default(2000),
    maxIssues: z.number().int().min(1).max(1000).default(200),
  })
  /**
   * ⚠️ 至少要有一本书的线索：**id 或者「出版社 + 年级 + 学期」**。
   *    两者都没有 = 不知道往哪本书里录，400 说清楚（不猜、不落"默认课本"）。
   */
  .refine((value) => value.textbookId !== undefined || value.publisher !== undefined, {
    message: '请给 textbookId，或者给 publisher（出版社 + 年级 + 学期 就是这本书）',
    path: ['publisher'],
  });

/** 查一本书某一册的三张表（各有多少条）。 */
export const listOverviewQuerySchema = z.object({
  textbookId: idSchema,
  gradeCode: gradeCodeSchema.optional(),
  semester: z.enum(SEMESTERS).optional(),
});

/**
 * **「选词造词」候选词查询**（2026-11-04）。
 *
 * 只给一个 `limit`：候选词按"含这个字"查，短词在前 —— 界面一次展示一小屏。
 */
export const wordCandidatesQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(60),
  /** 分批展示的起点（点「显示下一批」时传回来）。 */
  offset: z.coerce.number().int().min(0).default(0),
  /**
   * 只要「这个字在这个词里读这个音」的词（多音字按音选词）。
   * 省略 = 不按读音过滤（老调用方 / 家长端只读场景）。
   */
  reading: z.string().trim().max(32).optional(),
});

/** 查一个字的收录情况（"它出现在哪几张表上"）+ 各读音的造词数。 */
export const hanziListingsQuerySchema = z.object({
  includeReadings: queryBooleanSchema.default('true'),
});

/** 词语（`word`，含成语）。 */
export const createWordSchema = z.object({
  textbookId: idSchema,
  wordText: z
    .string()
    .trim()
    .min(1, '请填写词语')
    .max(32)
    .refine((v) => [...v].length <= 16, '词语最长 16 个字'),
  pinyin: wordPinyinSchema(128).optional(),
  category: z.enum(WORD_CATEGORIES).default('phrase'),
  fromTextbook: z.boolean().default(false),
  gradeCode: gradeCodeSchema.nullable().default(null),
  semester: z.enum(SEMESTERS).nullable().default(null),
  note: z.string().trim().max(200).default(''),
  /** 关联课文（课本词语表按课组织）。 */
  lessonIds: idListSchema.optional(),
  /**
   * **每个字在这个词里读哪个音**（按位置，从 0 开始；多音字按词定音）。
   *
   * 省略 = 服务层从整词拼音（`pinyin`）逐位推（`readingsFromWordPinyin`）。
   * 显式给值时：长度必须与词长一致，且每一项要么是空串（"这个音待填"），
   * 要么是这个字**确实有的读音**（内置字表的主读音/备选读音，或家长对该字的
   * 人工覆盖值）—— 否则报 400。
   *
   * 例：给「长大」建词并指定 `["cháng", "dà"]` 之后，
   * `GET .../hanzi/:id/readings` 会显示"长"有 cháng 与 zhǎng 两个音、
   * 各自有哪些词。
   */
  charReadings: z.array(singlePinyinSchema(32)).max(16).optional(),
});

export const updateWordSchema = z
  .object({
    /**
     * **改字**（2026-10-30 用户要求："造的词好像也不能修改？万一有错别字怎么办？"）。
     *
     * ⚠️ 只有**自己造的词**（`fromTextbook = false`）能改 —— 课本词语表是导入的事实，
     * 改字会破坏"课本顺序 / 收录"的口径。服务层会再判一次（`updateWord`）。
     */
    wordText: z
      .string()
      .trim()
      .min(2, '至少两个汉字')
      .max(32)
      .refine((v) => [...v].length <= 16, '词语最长 16 个字')
      .optional(),
    pinyin: wordPinyinSchema(128).nullable().optional(),
    category: z.enum(WORD_CATEGORIES).optional(),
    fromTextbook: z.boolean().optional(),
    gradeCode: gradeCodeSchema.nullable().optional(),
    semester: z.enum(SEMESTERS).nullable().optional(),
    note: z.string().trim().max(200).optional(),
    lessonIds: idListSchema.optional(),
    /** 同 `createWordSchema.charReadings`；给了就**整组替换**每个字的读音。 */
    charReadings: z.array(singlePinyinSchema(32)).max(16).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, '没有需要更新的内容');

/**
 * 只改**某个字在某个词里的读音**（`PATCH .../words/:wordId/readings`）。
 *
 * 为什么单独一个端点：这是"多音字按音造词"最高频的动作（家长发现"长大"读错了，
 * 只想改这一个音，不想动别的字段），而且它要按位置定位（`position`），
 * 与整词的 `PUT .../pinyin` 不是一回事。
 */
export const setWordCharReadingsSchema = z.object({
  /**
   * 逐位读音（长度必须与词长一致）。空串 = **清除这个位置的读音**
   * （回到"从整词拼音推"）。
   */
  readings: z.array(singlePinyinSchema(32)).min(1).max(16),
});

/**
 * 只改拼音覆盖（字/词通用）—— 界面上最高频的一个动作，单独给一个轻量入口。
 *
 * 词语的拼音较长的原因：整词拼音是"按词定音"的唯一入口，必须允许直接塞一整串。
 * 长度上限 128 对应"最多 16 个字 × 每字 8 字符（含声调）"。
 */
export const setHanziPinyinSchema = z.object({
  /** 空串 / null 都表示"清除人工覆盖，回到自动值"。 */
  pinyin: singlePinyinSchema(32).nullable(),
  /**
   * **这次是从哪一个位置（`hanzi_listing.id`）改的** —— 可省略。
   *
   * ## 为什么需要它（2026-10-21 用户报的那条 bug）
   *
   * 用户原话：
   *
   * > "我在「浏览与编辑」模块修改了一个字的拼音，点保存后，**外面的字的拼音没有
   * >  改变**，再点进去看，却是保存新的拼音的，就是外面没有更新。"
   *
   * 根因是**两个字段**：字卡显示 `listingReading ?? pinyin`（这个位置标定的音优先），
   * 而"改这个字的读音"写的是 `hanzi.pinyin`（字的默认音）。导入时每个位置的读音
   * 会被**快照**进 `hanzi_listing.reading`，于是"改字默认音"在任何卡片上都看不出来。
   *
   * 语义（用户 2026-10-21 定稿）：
   *   · `reading IS NULL` 的位置 —— 本来就用默认音 ⇒ 自动跟着变（不需要这个字段）；
   *   · 有显式 `reading` 的位置 —— **保持不变**（那是单独确认过的音）；
   *   · **但用户就是从某一张卡点进来的** ⇒ 那个位置也要跟着改，否则他改完还是
   *     看不见变化 —— 这就是这个字段。
   *
   * ⚠️ 只在**从字卡打开浮窗**时传；家长端没有这个写入口。
   * ⚠️ 不传 / `null` ⇒ 行为与改造前**逐字一致**（只改 `hanzi.pinyin`）。
   */
  listingId: z.string().uuid().nullable().optional(),
});

/**
 * 只改**教学口径的「多音字」标注**（`PUT .../hanzi/:hanziId/polyphonic`）。
 *
 * ⚠️ 这是**人工标注**，不是自动判定：`hanzi.polyphonic` 默认 `false`（单音字）。
 *    「内置拼音表里有第二个读音」是**技术层**的另一件事（`lookupPinyin()` /
 *    `availableReadings()`），保留给读音选择、按音造词、听写多音字专项用。
 *    之所以分开：那个表把变调（一 yī/yí/yì、不 bù/bú）、古音（七 qī/qí）、
 *    生僻音（上 shàng/shǎng）都算成"读音"，自动判定会把 280 个一年级生字
 *    标出 134 个多音字。
 */
export const setHanziPolyphonicSchema = z.object({
  polyphonic: z.boolean(),
});

/**
 * 改「**这个字隐藏了哪几个读音**」（`PUT /admin/dictation/hanzi/:hanziId/hidden-readings`）。
 *
 * ## 这是什么（用户 2026-10-18 的定稿）
 *
 * > "**隐藏不常用的音**：如果某个音是不常用的，就把它隐藏起来。但是想看的话，
 * >  也能点开来看。" → 紧接着："**第一个，隐藏读音要存服务器。**"
 *
 * 所以它从"本机记忆"升级成**站点字表数据的一部分**：管理员在这台设备上隐藏的音，
 * 家里别的手机、别的浏览器看到的也是隐藏后的样子（家长端只读，但**看得到**）。
 *
 * ## 为什么只是 `string[]` 而不是"每条带原因 / 时间"
 *
 * 隐藏只影响**显示与选择**：已有的词、它们在 `word_hanzi.reading` 里的读音、
 * 听写取词与播放，一个字都不动（这条语义从第一版起就没变）。
 * 既然它不参与任何判定，就不需要审计每一格 —— 审计记的是"谁在什么时候把
 * 这个字的隐藏集合改成了什么"（整组替换，见路由层）。
 *
 * ## 校验分工（两层，各有各的理由）
 *
 *   · **这一层（zod）**：形状 —— 必须是数组、每项是小写拼音、最多 16 个；
 *   · **服务层**（`normalizeHiddenReadings()`）：语义 —— 每一项必须是
 *     `availableReadings(charText)` 里的读音，并顺手去重 / 丢掉空串。
 *
 * 为什么"必须是这个字的读音"不放在 zod：zod 拿不到那个字（`charText` 在库里），
 * 而这条判定与"按音造词""读音选择"必须**同一份实现**（shared 的那个纯函数）。
 */
export const setHanziHiddenReadingsSchema = z.object({
  /** 空数组 = **全部显示**（默认值，也是老数据的口径）。 */
  hidden: z
    .array(singlePinyinSchema(32))
    .max(MAX_HIDDEN_READINGS, `最多隐藏 ${MAX_HIDDEN_READINGS} 个读音`),
});

export const setWordPinyinSchema = z.object({
  pinyin: wordPinyinSchema(128).nullable(),
});

// ---------------------------------------------------------------- 导入

/**
 * 导入预览（`POST /students/:id/dictation/import/preview`）。
 *
 * `text` 是**整块粘贴**的原文（制表符/逗号/空格/顿号分隔，格式见
 * `shared/dictation/import-parser.ts` 顶部说明）。除了 50 万字符的总量上限，
 * 刻意不加更多格式约束：格式由解析器逐行判断并逐行报错，而不是整批拒绝。
 */
export const importPreviewSchema = z.object({
  text: z.string().min(1, '请粘贴要导入的内容').max(500_000, '一次最多 50 万字符'),
  /** 默认课本（文本里没写课本列时用它）。可以是 id，也可以是出版社代号/名称。 */
  textbook: z.string().trim().max(64).optional(),
  /**
   * 默认年级 / 学期（文本里没写时用它）。**课本身份的一列**。
   *
   * ⚠️ 课本按册建：给的是名字时必须能确定是哪一册；给 id 时可以直接用书的册别。
   * ⛔ 版次已经不是身份（2026-10-18 彻底去掉），这里没有 edition 这一项；
   *    文本里即使还有「版次」列，也会被**忽略且不报错**。
   */
  gradeCode: gradeCodeSchema.optional(),
  semester: z.enum(SEMESTERS).optional(),
  /**
   * **默认表类型**（文本里没有表名行、也没有表名列时用它）。
   *
   * 这是"按书 + 按表录入"在通用导入入口上的落点：家长在界面上选了
   * 「三年级上册 · 识字表」之后整块粘，就不必在每一行写表名。
   * 省略 = 不知道（字行不写收录 —— **不替家长猜一张表**）。
   */
  listType: z.enum(LIST_TYPES).optional(),
  /** 词条的默认类别（录词语表时用；省略按"词语"）。 */
  wordCategory: z.enum(WORD_CATEGORIES).optional(),
  /** 逐行错误超过这个数量就停止继续收集（防止一份错格式的粘贴刷爆响应）。 */
  maxIssues: z.number().int().min(1).max(1000).default(200),
});

/** 导入提交：与预览同一份入参，外加"最多写多少行"的保护。 */
export const importCommitSchema = importPreviewSchema.extend({
  /** 限制一次真正写入的条数（超过时只写前 N 条并在结果里标注）。 */
  maxRows: z.number().int().min(1).max(5000).default(2000),
});

// ---------------------------------------------------------------- 听写取词

/**
 * 取词（`POST /students/:id/dictation/words`）：这是下一步"生成录音 + 听写"的基础。
 *
 * 筛选条件之间是 **AND**；同一条件内给数组是 **OR**（例如
 * `gradeCodes: ['primary_3']`）。路径里的 `:id` 是后端判权限用的那个孩子
 * （必须能看这个孩子），同时也给"没传 `gradeCodes` 时"提供默认年级。
 *
 * ⚠️ **这是后端约束，不要把它当成前端概念**：字表是全家的（没有 `student_id`），
 *   前端录入页只在内部挑一个孩子发请求，界面上**不提**这件事，而是让家长在
 *   选词预览里**直接选年级**（`gradeCodes`，可多选；见 `DictationPickPanel`）。
 */
export const dictationWordQuerySchema = z.object({
  textbookId: idSchema.optional(),
  /**
   * **按若干本书一起筛**（迁移 0020 的批量任务用，2026-10-28）。
   *
   * 用户的批量任务是"一次性布置**一本书或者几本书**"，所以取词必须能一次给多册。
   * 与 `textbookId` 的关系：给了 `textbookIds` 就用它（数组 OR），否则看 `textbookId`
   * —— 两个都给时以数组为准（数组是更明确的那一个），见 `wordFilters`。
   */
  textbookIds: z.array(idSchema).max(20).optional(),
  gradeCodes: z.array(gradeCodeSchema).max(12).optional(),
  semesters: z.array(z.enum(SEMESTERS)).max(3).optional(),
  lessonIds: idListSchema.optional(),
  /** 词语类别。 */
  categories: z.array(z.enum(WORD_CATEGORIES)).max(3).optional(),
  /** 是否只取课本词语表里的词。 */
  fromTextbookOnly: z.boolean().optional(),
  /**
   * **按「表」筛词**（识字表 / 写字表 / 词语表）—— 第五步新增（缺口 1 的修复）。
   *
   * 语义（**ANY，不是 ALL**）：只要这个词里**有任何一个字**被收录进了选中的表
   * （且在选中的年级/学期范围内），这个词就取得到。
   *
   * 为什么是 ANY 而不是 ALL：家长的心智是"我今天要听写识字表里的字组成的词"，
   * 而一个词里往往有一部分字还没收录（家长只录了识字表，写字表还没录）——
   * ALL 会让这些词**静默消失**，家长看到的是"我明明选了识字表，一个词都没有"。
   * ANY 是"包含关系"的自然语义，扣不住的时候我们宁可多给一个词（家长在预览里
   * 一眼能看出来），也不要少给。
   *
   * 与 `fromTextbookOnly` 的关系：后者是**旧口径**（`word.from_textbook`，
   * 只看词自己是不是来自课本词语表）；`listTypes` 是**新口径**（看这个字被收录
   * 在哪张表上）。两者可以同时给（AND），也可以都不给。
   */
  listTypes: z.array(z.enum(HANZI_LIST_TYPES)).max(2).optional(),
  /**
   * **只听某个读音的词**（第五步新增 · 缺口 2 的修复）。
   *
   * 每个元素是 `{ char?, reading }`：
   *   · `{ reading: 'cháng' }` —— 任何一个字在这些词里读 cháng 就算；
   *   · `{ char: '长', reading: 'cháng' }` —— **只认"长"这个字**读 cháng 的词
   *     （「长大」符合，「长处」如果录的是 cháng 也符合；「校长」读 zhǎng 不符合）。
   *
   * 多个元素之间是 **OR**（与其它条件之间是 AND）。
   */
  readings: z
    .array(
      z.object({
        char: z
          .string()
          .trim()
          .refine((v) => [...v].length === 1, '必须正好一个汉字')
          .optional(),
        reading: singlePinyinSchema(32).refine((v) => v !== '', '读音不能是空串'),
      }),
    )
    .max(8)
    .optional(),
  /**
   * **只看多音字相关的词**：词里至少有一个字的读音**不是它的默认读音**
   * （`word_hanzi.reading <> hanzi.pinyin`）。
   *
   * 这就是"专门听写多音字"在取词层的落点：`{ isPolyphonic: true }` 得到的是
   * "有字在这里读了非默认音"的那些词（长大 cháng、校长 zhǎng、银行 háng……）。
   * 注意它**不按内置字表有几个读音判**——"默认读音之外的音"才是听写上真正的风险点。
   */
  isPolyphonic: z.boolean().optional(),
  /** 随机取几个。省略 = 取全部（受 `limit` 限制）。 */
  randomCount: z.number().int().min(1).max(200).optional(),
  /** 不随机时的取词上限（默认 200，最大 1000）。 */
  limit: z.number().int().min(1).max(1000).default(200),
  /**
   * 取词来源：
   *   · `all`（默认）—— 全部词；
   *   · `wrong_first` —— **错词优先**：先把错词本里还该复习的词放进来，
   *     名额没满再补新词（排序规则见 `docs/接口契约对齐.md` 的第四步章节）；
   *   · `wrong` —— **只听写错词**：只取错词本里的词，一个都不补。
   *
   * ⚠️ 三者的**筛选条件（年级/学期/课文/类别）是同一套** —— 错词同样要落在家长
   *    选中的范围内，否则"我只选了三年级上册"却听写出别的年级的错词。
   *    排程只看"该不该复习"，不该成为绕过筛选的理由。
   */
  source: z.enum(['all', 'wrong_first', 'wrong']).default('all'),
  /**
   * 错词候选是否**只取已到期**（`next_review_at <= now`）。
   *
   * 默认 `false`：错词本里没到期的词也可以被主动听写。
   * 「今天该复习的错词」那条入口（`POST .../mistakes/session`）会把它设成 `true`。
   */
  wrongDueOnly: z.boolean().optional(),
  /** 同一份随机结果可复现（传同一个 seed 得到同一组词）。省略 = 每次不同。 */
  seed: z.number().int().min(0).max(2_147_483_647).optional(),
  /**
   * 不随机时的**排序口径**（迁移 0020 的批量任务用，2026-10-28）。
   *
   *   · `text`（默认，既有行为）—— 按词面文字升序；
   *   · `textbook` —— **按课本顺序**（`word.sort_order`，即导入时那张表的行序）。
   *
   * 批量任务要求"顺序可选：按课本顺序 / 随机"，而"按课本顺序"如果落到词面
   * 升序上，孩子听到的就是一个按汉字码点排的名单 —— 与课本无关。
   * 默认保持 `text`，不改动任何已有调用方。
   */
  orderBy: z.enum(['text', 'textbook']).default('text'),
  /** 是否连同拼音一起返回（默认返回）。 */
  includePinyin: z.boolean().default(true),
});

/** 字 → 词：查一个字的组词（`GET /dictation/hanzi/:id/words`）。 */
export const hanziWordsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

/**
 * 「浏览与编辑」的字表 / 词表**一次取回**的最大条数。
 *
 * ## 为什么从 2000 提到 20000（2026-10-20）
 *
 * 用户原话：
 *
 * > "查询出那些字的列表之后，不是每 100 个显示一批嘛，我觉得老是去点那 100 个
 * >  也挺麻烦的，你就**一次性全部显示**吧，有多少个就显示多少个，**取消那个 100 的限制**。"
 *
 * 于是前端不再分页（`BROWSE_PAGE_SIZE` / 「再显示 100 个」已删），**一次取回、一次渲染**。
 * 这个上限就是"一次能显示多少条"：
 *
 *   · 一册字表 ~280 条；20000 ≈ 70 册，覆盖"全站几百到几千"并留足余量；
 *   · 不设"不限"：`limit` 直接进 SQL 的 `LIMIT`，无上限意味着一次查询可能把整库
 *     拉进内存再序列化，是真实的 OOM 风险，而不是"更自由"；
 *   · 真超过时界面会**明说**「服务器这次只返回了 N 条（共 M 条）」（`describeBrowseTotal`），
 *     不会给一个点了没反应的按钮，也不会假装已经全部显示。
 *
 * ⛔ 这个值必须与前端请求的 `limit` 同源（web 用 `DICTATION_BROWSE_LIMIT_MAX`），
 *    不要在一处改大、另一处还写死旧值。
 */
export const DICTATION_BROWSE_LIMIT_MAX = 20_000;

/** 列表分页（课文 / 汉字 / 词语列表共用，全部走查询串）。 */
export const dictationListQuerySchema = z.object({
  textbookId: idSchema.optional(),
  /**
   * **按「出版社」筛**（2026-10-18 用户要求：「浏览与编辑」的筛选里那一格
   * 从"课本版本"改成"出版社"，下拉只列去重后的出版社）。
   *
   * 语义：匹配 `textbook.publisher`（大小写不敏感）。与 `gradeCode` / `semester`
   * 组合起来就是"哪一册"（一本书 = 一个具体的册），但单独用也成立 ——
   * "这家出版社的所有册"。⛔ 不要再要求前端先解析出 `textbookId`：
   * 出版社 A 下有多册时，"出版社 + 年级/学期任缺一个"用 `textbookId` 表达不出来。
   */
  publisher: z.string().trim().min(1).max(32).optional(),
  gradeCode: gradeCodeSchema.optional(),
  semester: z.enum(SEMESTERS).optional(),
  lessonId: idSchema.optional(),
  lessonNo: z.coerce.number().int().min(0).max(999).optional(),
  /**
   * **按表筛**（识字表 / 写字表 / 词语表）。
   *
   * 三种取值的语义**不同**（服务层各走一支，见 `dictation.service.ts`）：
   *   · `shizi` / `xiezi` —— 汉字列表筛"收录在哪张字表"；词语列表筛"词里有字
   *     收录在这张字表上"（与取词的语义一致）。
   *   · `cibiao` —— **词语表不是字表**：汉字列表此时返回空（界面上字块也整个
   *     不渲染）；词语列表筛"这个词本身就来自课本词语表"（`word.from_textbook`）。
   *
   * ⚠️ 2026-10-19：这里是 `LIST_TYPES`（三个）而不是 `HANZI_LIST_TYPES`（两个）——
   *    用户报的 bug 就是「在哪张表上」下拉里没有「词语表」。值沿用后端既有枚举
   *    `cibiao`（见 `shared/dictation/listing.ts`），**没有新造值**。
   * ⚠️ 旧参数 `hanziCategory`（`type1`/`type2`）**已随 0009 一起删掉**：
   *    `hanzi.category` 列没有了，留着这个参数只会让"筛了却没生效"看起来正常
   *    （本项目反复踩过的那条线）。
   */
  listType: z.enum(LIST_TYPES).optional(),
  /** 词语类别（只有词语列表用）。 */
  wordCategory: z.enum(WORD_CATEGORIES).optional(),
  /** 只取课本词语表 / 只看人工改过拼音的 / **只看多音字**的。 */
  fromTextbookOnly: queryBooleanSchema.optional(),
  overriddenOnly: queryBooleanSchema.optional(),
  /** 只看多音字（字列表：这个字有多个读音；词列表：词里有字读了非默认音）。 */
  polyphonicOnly: queryBooleanSchema.optional(),
  /** 只看某个字**某个读音**的词（与取词的 `readings` 同口径，单个更常用）。 */
  reading: singlePinyinSchema(32).optional(),
  /** 按文本模糊搜索。 */
  keyword: z.string().trim().max(32).optional(),
  /**
   * 一页最多 `DICTATION_BROWSE_LIMIT_MAX`（= 20000）条。
   *
   * ⚠️ 2026-10-19：原来是 500，当天提到 2000（前端每次多渲染 100 个）。
   *    2026-10-20 用户要求**一次全部显示**，前端分页已删 —— 见
   *    `DICTATION_BROWSE_LIMIT_MAX` 的注释（为什么是 20000、为什么不做"不限"）。
   */
  limit: z.coerce.number().int().min(1).max(DICTATION_BROWSE_LIMIT_MAX).default(100),
  offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
});

/** 课本列表查询。 */
export const listTextbooksQuerySchema = z.object({
  subject: z.enum(DICTATION_SUBJECTS).optional(),
});

// ---------------------------------------------------------------- 听写会话（第三步）

/**
 * 听写会话的状态。
 *
 * `graded`（已批改）是**第四步**的值 —— 现在就在枚举里，是因为第四步只该改
 * **一个 UPDATE**，而不是"再补一次状态、再补一次界面分支"。本轮任何路径都
 * **不会**把会话置成它（见 `dictation.routes.ts` 的 `PATCH` 白名单）。
 */
export const DICTATION_SESSION_STATUSES = ['pending', 'in_progress', 'completed', 'graded'] as const;
export type DictationSessionStatus = (typeof DICTATION_SESSION_STATUSES)[number];

/** 会话里一道题的**播放**状态（与第四步的"对错"是两件事，见 schema.ts 的注释）。 */
export const DICTATION_QUESTION_STATUSES = ['pending', 'played', 'skipped'] as const;
export type DictationQuestionStatus = (typeof DICTATION_QUESTION_STATUSES)[number];

/**
 * 选词条件的快照（原样存 `dictationWordQuerySchema` 的入参）。
 *
 * ⚠️ 它**原样透传**给 `pickDictationWords()`，所以这里**不能**加"只有这些键"的
 *   严格校验 —— 加了以后每次给取词加一个筛选条件，都得同时改这里，
 *   而漏改的表现是"新条件在预览里能用、一布置听写就 400"。
 *   这里只挡住最外层的形状（必须是对象），字段级的校验在取词那一层。
 */
export const sessionPickCriteriaSchema = z.record(z.unknown());

/**
 * 新建听写会话：**取词条件 + 节奏 + 标题**。
 *
 * 刻意**不**接受"客户端把选好的词贴进来"（那会变成第二套取词逻辑，且与服务端
 * 取的词不一致时就再也说不清是哪一边错了）。词由**服务端**用
 * `pickDictationWords()` 取——这一步只是把条件与节奏交给它。
 */
export const createDictationSessionSchema = z.object({
  /** 标题（"10月15日 语文听写"）。留空由服务端按日期生成。 */
  title: z.string().trim().max(64).default(''),
  pick: sessionPickCriteriaSchema.default({}),
  /** 节奏；省略时用 `DEFAULT_DICTATION_TEMPO`（见 tempo.ts）。 */
  tempo: dictationTempoSchema.optional(),
});

/** 改节奏 / 标题 / 状态。三个都可选，但至少给一个（否则是一次没有意义的写库）。 */
export const updateDictationSessionSchema = z
  .object({
    title: z.string().trim().max(64).optional(),
    tempo: dictationTempoSchema.optional(),
    /**
     * ⚠️ 只允许 `pending → in_progress → completed` 这条线上来回（以及回退到
     * `pending`）。`graded` **不在**白名单里：它是第四步"批改完成"的落点，
     * 在本轮可以被写进来的话，界面上就会出现一个**永远没有批改内容的已批改会话**。
     */
    status: z.enum(['pending', 'in_progress', 'completed']).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, '没有需要更新的内容');

/** 会话列表查询（分页 + 可选按状态筛）。 */
export const listDictationSessionsQuerySchema = z.object({
  status: z.enum(DICTATION_SESSION_STATUSES).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
});

/**
 * 记录**播放进度**（哪几题读过了 / 跳过了）。
 *
 * 用批量数组而不是"一题一次请求"：一节课 20 个词，逐个 PATCH 会打出 20 个请求，
 * 而这件事**完全不重要**（丢一两条只影响回看时"读过没有"的标记）。
 * 一次请求带上全部变化，弱网下也只在最后发一次。
 *
 * ⚠️ 它**只**写 `status`（pending/played/skipped）。**没有**"对/错"的入口：
 * 那是第四步（`mark` 列 + 批改接口），本轮一个都不提供 —— 界面上也就不会
 * 出现"点了没反应"的批改按钮。
 */
export const updateDictationProgressSchema = z.object({
  questions: z
    .array(
      z.object({
        questionId: idSchema,
        status: z.enum(DICTATION_QUESTION_STATUSES),
      }),
    )
    .min(1, '至少要给一道题')
    .max(200, '一次最多更新 200 道题'),
});

// ---------------------------------------------------------------- 家长录音（覆盖 TTS）

/**
 * 上传一段录音（覆盖这个词的 TTS 朗读）。
 *
 * `audioBase64` **不带** `data:` 前缀（存库的也是纯 base64，见 schema.ts）。
 * 前缀在这里被显式拒绝而不是"顺手剥掉"：剥掉会让"客户端拼错了前缀"这类错误
 * 永远看不见，而它是那种"上传成功但播不出声"的典型来源。
 */
export const uploadDictationAudioSchema = z.object({
  mimeType: z
    .string()
    .trim()
    .min(1, '缺少录音格式')
    .max(64)
    .regex(/^audio\/[A-Za-z0-9.+-]+(\s*;\s*codecs=[A-Za-z0-9._,+-]+)?$/, '录音格式不对（应形如 audio/webm）'),
  audioBase64: z
    .string()
    .min(1, '这段录音是空的')
    .max(
      DICTATION_AUDIO_MAX_BASE64,
      `录音太长：base64 最多 ${DICTATION_AUDIO_MAX_BASE64} 个字符（约 ${Math.round(
        base64DecodedBytes(DICTATION_AUDIO_MAX_BASE64) / 1024,
      )} KB 音频）`,
    )
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, '录音数据不是合法的 base64（不要带 data: 前缀）'),
  /** 原始字节数（浏览器 `Blob.size`）；缺省时服务端按 base64 长度估算。 */
  byteSize: z.number().int().min(0).max(10_000_000).optional(),
  /** 真实时长（毫秒）；拿不到就**不传**（存 `NULL`，与"0 毫秒"是两件事）。 */
  durationMs: z.number().int().min(0).max(120_000).nullable().optional(),
});

/** 查一个词的录音（也是"这个词有没有录音"的判定接口）。 */
export const dictationAudioQuerySchema = z.object({
  /** `json` = 返回 base64 一起给（默认 `dataUrl`：直接喂 `<audio src>`）。 */
  format: z.enum(['dataUrl', 'json']).default('dataUrl'),
});

/**
 * 录音清单的分页（`GET .../dictation/audios`）。
 *
 * 单独一个 schema 而不是复用会话列表那个：两者的**筛选条件根本不是一回事**
 * （会话按状态筛，录音没有状态），复用会在下次给某一侧加筛选项时
 * 悄悄改变另一侧的行为。分页默认值保持一致（20/0），界面上的手感才一样。
 */
export const listDictationAudiosQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
});

// ---------------------------------------------------------------- 家长批改（第四步）

/**
 * 批改结果（`dictation_question.mark`）。
 *
 * | 取值 | 意思 |
 * | --- | --- |
 * | `correct` | 对了 |
 * | `wrong` | 写了，但写错了 |
 * | `missing` | **没写出来**（空着 / 不会写） |
 *
 * ⚠️ `null`（没批改）**不在这个 schema 里**：提交批改是"给出判定"，
 *   "把判定擦掉"没有对应的界面动作（家长想改就是改成另一个判定）。
 *   数据库那一列可空，是因为**还没批改的历史题**需要它。
 *
 * 为什么不用布尔：用户原话是"没有听写出来**或者**错的词"——这是两件事。
 * `is_correct boolean` 装不下它们（写错 vs 根本不会写，辅导方式完全不同）。
 */
export const dictationMarkSchema = z.enum(DICTATION_MARKS);

/**
 * 提交批改（`POST .../sessions/:sessionId/grade`）。
 *
 * ## 为什么是"整套提交"而不是"一题一个请求"
 *
 * 家长批改的真实动作是"**默认全对，只点错的那几题**，然后提交一次"。
 * 所以请求体给**全部题目**的判定，没给到的题按 `correct` 补齐
 * （`mark` 可省略 = 对了，这就是"只点错的"在协议层的落点）。
 *
 * ## 幂等
 *
 * 同一个请求体提交两次，错词计数**不会**翻倍：
 *   1. 每次提交前先删掉这道题上一次的判定（`mark` / `counted_mark`）；
 *   2. 只有"这次的判定 ≠ 上一次生效的判定"才去动错词本。
 * 见 `packages/shared/src/dictation/schedule.ts` 的文件头。
 */
export const gradeDictationSessionSchema = z.object({
  marks: z
    .array(
      z.object({
        questionId: idSchema,
        /** 省略 = `correct`（"默认全对，只点错的"）。 */
        mark: dictationMarkSchema.optional(),
      }),
    )
    .min(1, '至少要给一道题')
    .max(500, '一次最多批改 500 道题'),
});

// ---------------------------------------------------------------- 错词本（第四步）

/** 错词本的筛选（`GET .../dictation/mistakes`）。 */
export const listDictationMistakesQuerySchema = z.object({
  /**
   * 只看**今天该复习的**（`next_review_at <= 现在`）。
   *
   * ⚠️ 默认 `false` —— 错词本默认显示**全部**还没掌握的词（"这个词一共错过几次"
   *    是家长最常问的），而"今天该复习"是一个**筛选**，不是一个隐含的过滤。
   *    默认过滤掉未到期的词，会让家长以为"我登记的错词怎么不见了"。
   */
  dueOnly: z
    .enum(['true', 'false', '1', '0'])
    .transform((v) => v === 'true' || v === '1')
    .default('false'),
  /**
   * 是否含已掌握（消解）的词。默认 `false`（错词本是"还没搞定的词"）。
   * 想回看"哪些词已经掌握了"时传 `true`。
   */
  includeResolved: z
    .enum(['true', 'false', '1', '0'])
    .transform((v) => v === 'true' || v === '1')
    .default('false'),
  limit: z.coerce.number().int().min(1).max(200).default(100),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
});

/**
 * 手动标记"已掌握" / 撤销（`PATCH .../dictation/mistakes`）。
 *
 * 按 **wordId 批量**而不是按 mistake 行 id：界面上家长选中的是**词**
 * （他看到的列表就是词），而错词行的 id 是内部标识 —— 让前端去记它，
 * 只会多一处"id 对不上就什么都没发生"的坑。
 *
 * 这个操作**幂等**：已经消解的词再标一次不会有额外影响（返回的
 * `changed` 会如实说改了几行）。
 */
export const updateDictationMistakesSchema = z.object({
  wordIds: z.array(idSchema).min(1, '至少要给一个词').max(200, '一次最多处理 200 个词'),
  /** `true` = 标记已掌握；`false` = 撤销（这个词又不会写了，重新排进来）。 */
  resolved: z.boolean(),
});

/**
 * 从错词本**直接开始一次听写**（`POST .../dictation/mistakes/session`）。
 *
 * 与「布置听写」的差别只有**取词条件**：来源固定成错词。之所以单独一个端点
 * 而不是让前端自己拼 `pick: { source: 'wrong' }`：
 *   1. `pick` 的形状在会话接口那一层是"原样透传"的（故意不加严格校验），
 *      前端自己拼就有拼错的可能，而拼错的表现是"取到一屋子不相关的词"；
 *   2. 这里要顺带把**这次是从错词本发起的**这件事写进会话标题与条件快照，
 *      家长在历史列表里一眼能看出它是复习。
 */
export const startDictationFromMistakesSchema = z.object({
  /** `due_only` = 只复习今天到期的；`all` = 错词本里全部还没掌握的。 */
  mode: z.enum(['due_only', 'all']).default('due_only'),
  /** 最多取几个词（与取词接口同一口径）。 */
  limit: z.number().int().min(1).max(200).default(50),
  /** 标题；留空由服务端生成（"10月15日 · 错词复习"）。 */
  title: z.string().trim().max(64).default(''),
  /** 节奏；省略时用 `DEFAULT_DICTATION_TEMPO`。 */
  tempo: dictationTempoSchema.optional(),
});

// ---------------------------------------------------------------- 批量任务（迁移 0020）

/**
 * 批量任务的状态。
 *
 * 用户原话："孩子不能结束整个任务，家长可以结束、暂停或删除整个任务。"
 * 所以这三个值是**家长的三种动作**，孩子端只能读取与推进 session：
 *
 * | 值 | 谁写的 | 含义 |
 * | --- | --- | --- |
 * | `active` | 家长 | 进行中，孩子可以点「开始听写」 |
 * | `paused` | 家长 | 暂停：孩子点「开始听写」会被拒（`DICTATION_ASSIGNMENT_INACTIVE`） |
 * | `finished` | 家长 | 已结束：不再往下发新的一批，进度与错词**全部保留** |
 */
export const DICTATION_ASSIGNMENT_STATUSES = ['active', 'paused', 'finished'] as const;
export type DictationAssignmentStatus = (typeof DICTATION_ASSIGNMENT_STATUSES)[number];

/**
 * 每次听写几个的合法范围（与数据库 `ck_dictation_assignment_batch_size` 同口径）。
 *
 * 上限 200 不是随便定的：一次听写 200 个词已经远超一个孩子一节课的量，
 * 再往上就只是把界面变成一个不会结束的播放器。
 */
export const DICTATION_ASSIGNMENT_BATCH_SIZE = { min: 1, max: 200, fallback: 10 } as const;

/**
 * **布置一个批量任务**（`POST /students/:id/dictation/assignments`）。
 *
 * 与「布置一次听写」（`createDictationSessionSchema`）的关键差别：
 *   · 这里**不取词** —— 只记下"范围 + 每次几个 + 顺序 + 节奏"，
 *     具体哪 10 个词是孩子每次点「开始听写」时由 `nextBatch` 现取的；
 *   · 范围**至少要给一个**（几本书，或者年级 + 学期）：范围全空的任务
 *     等于"整库随机出词"，家长完全无法预期孩子会听到什么，所以直接拒。
 */
export const createDictationAssignmentSchema = z
  .object({
    /** 标题（"二年级上册 · 词语听写"）。留空由服务端按范围生成一句人话。 */
    title: z.string().trim().max(64).default(''),
    /** 范围：明确指定的几本书（可空，空了就看 `gradeCode` + `semester`）。 */
    textbookIds: z.array(idSchema).max(20, '一个任务最多挂 20 本书').default([]),
    /** 范围兜底：整年级（`textbookIds` 为空时必须有）。 */
    gradeCode: gradeCodeSchema.optional(),
    semester: z.enum(SEMESTERS).optional(),
    /** 取词类别：省略 = 不限（词 + 字都取）。 */
    category: z.enum(WORD_CATEGORIES).optional(),
    /** 每次听写几个（1~200，默认 10 —— 用户原话的例子就是 10）。 */
    batchSize: z
      .number()
      .int()
      .min(DICTATION_ASSIGNMENT_BATCH_SIZE.min)
      .max(DICTATION_ASSIGNMENT_BATCH_SIZE.max)
      .default(DICTATION_ASSIGNMENT_BATCH_SIZE.fallback),
    /** `false` = 按课本顺序；`true` = 随机。 */
    shuffle: z.boolean().default(false),
    /** 节奏；省略时用 `DEFAULT_DICTATION_TEMPO`。 */
    tempo: dictationTempoSchema.optional(),
  })
  .refine(
    (v) => v.textbookIds.length > 0 || v.gradeCode !== undefined,
    '请至少选一本书，或者选一个年级 —— 范围全空的任务，孩子会听到与课本无关的词',
  );

/** 改任务（家长）：暂停 / 恢复 / 结束，以及标题、每次几个、顺序、节奏。 */
export const updateDictationAssignmentSchema = z
  .object({
    title: z.string().trim().max(64).optional(),
    batchSize: z
      .number()
      .int()
      .min(DICTATION_ASSIGNMENT_BATCH_SIZE.min)
      .max(DICTATION_ASSIGNMENT_BATCH_SIZE.max)
      .optional(),
    shuffle: z.boolean().optional(),
    tempo: dictationTempoSchema.optional(),
    /**
     * 状态：`active` 恢复 / `paused` 暂停 / `finished` 结束。
     *
     * ⚠️ 从 `finished` 回到 `active` 也允许（家长误点了结束要能救回来）：
     *    这个字段**只是"发不发新的一批"的开关**，不牵扯任何已落库的历史事实，
     *    所以不需要一条不可逆的状态机。
     */
    status: z.enum(DICTATION_ASSIGNMENT_STATUSES).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, '没有需要更新的内容');

/** 任务列表查询（`GET .../dictation/assignments`）。 */
export const listDictationAssignmentsQuerySchema = z.object({
  /** 只看某个状态（省略 = 全部，家长端要能看到已结束的任务）。 */
  status: z.enum(DICTATION_ASSIGNMENT_STATUSES).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
});

